import { Currency } from '@/api/types/public';
import { ECouponType, ECouponUIType } from '@/utils/enums/coupon';
import { env } from '@/utils/env';
import { isNumber, subtract } from '@/utils/math';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { ICouponProps } from './type';

interface TFunction {
  (key: string, options?: any): string;
}
function getDiscountText(coupon: any, t: TFunction, currency: any, isNeedTranslate = true): string {
  const _couponType = couponType(coupon);

  if (_couponType === 4 && coupon.model_type === 4) {
    return t('free');
  }

  const { discounts_value, threshold_product_price } = getCouponDetails(coupon);
  const value = discounts_value || threshold_product_price;

  const formatMap = {
    1: {
      // 折扣
      withToken: isNeedTranslate ? `${value}%<br/>${t('off')}` : `${value}%<br/>OFF`,
      withoutToken: isNeedTranslate ? `${value}% ${t('off')}` : `${value}% OFF`,
    },
    2: {
      // 满减
      withToken: isNeedTranslate
        ? `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)}<br/>${t('off')}`
        : `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)}<br/>OFF`,
      withoutToken: isNeedTranslate
        ? `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)} ${t('off')}`
        : `${formatPriceNumberWithSymbol(`${value}`, currency, false, 2, false)} OFF`,
    },
  };

  const format = formatMap[_couponType] || formatMap[2]; // 默认使用满减格式
  return format.withToken;
}

function couponType(coupon: any) {
  const way = Number(coupon.way);
  const typeMap = {
    1: 1, // 折扣
    5: 1, // 折扣
    2: 2, // 满减
    3: 3, // 指定价
    4: 4, // 免费
  };

  return typeMap[way] || 0;
}

function getCouponDetails(coupon: any) {
  const _couponType = couponType(coupon);
  const detailsMap = {
    1: 'discounts_details',
    2: 'full_discounts_details',
    3: 'appoint_discounts_details',
    4: 'free_discounts_details',
  };

  return coupon[detailsMap[_couponType]] || {};
}

function getCouponTitle(coupon: any, t: TFunction, currency: any): string {
  const type = getCouponType(coupon);

  // TODO: 优惠券翻译这里的代码实在是太脏了，原因是因为产品逻辑定义的策略不同，有些地方需要翻译，有些地方不需要翻译，导致代码非常难看，后续看看是否能够优化产品逻辑从而优化代码

  // 兑换券
  if (type === ECouponType.EXCHANGE) {
    const couponDetails = getCouponDetails(coupon);
    const { number } = couponDetails;

    if (+number === 1) {
      return t('freeFrameChoice');
    }

    return t('buyGetFree', { number });
  }

  // 运费券
  if (type === ECouponType.SHIPPING) {
    const couponDetails = getCouponDetails(coupon);
    const { threshold_product_price } = couponDetails;
    return t('ordersOver', {
      price: formatPriceNumberWithSymbol(`${threshold_product_price}`, currency, false, 2, false),
    });
  }

  const siteWide = t('sitewideSale');
  const details = getCouponDetails(coupon);
  const { discounts_threshold_type, discounts_threshold = 1, discounts_value = 1 } = details;
  const _couponType = couponType(coupon);
  let discountThresholdText;
  if (_couponType === 1) {
    // 折扣
    // discounts_threshold_type 一次性折扣使用门槛 类型 1满金额减 2满件数减
    const thresholdType = Number(discounts_threshold_type);
    const thresholdValue = Number(discounts_threshold);
    if (thresholdType === 1) {
      discountThresholdText =
        thresholdValue === 0.01
          ? siteWide
          : t('ordersOver', {
              price: formatPriceNumberWithSymbol(`${thresholdValue}`, currency, false, 2, false),
            });
    } else {
      discountThresholdText =
        thresholdValue === 1 ? siteWide : t('itemsPlus', { number: thresholdValue });
    }
  } else if (
    isNumber(discounts_threshold) &&
    isNumber(discounts_value) &&
    subtract(discounts_threshold, discounts_value).toNumber() === 0.01
  ) {
    // 满减，门槛为1元时
    discountThresholdText = siteWide;
  } else {
    discountThresholdText = t('ordersOver', {
      price: formatPriceNumberWithSymbol(`${discounts_threshold}`, currency, false, 2, false),
    });
  }
  // 登录以后的文案，需要在前面加上For Orders(件数优惠折扣除外)
  if (discountThresholdText !== siteWide && !discountThresholdText.includes(t('above'))) {
    discountThresholdText = `${discountThresholdText}`;
  }
  if (_couponType === 3) {
    discountThresholdText = t('onSelectedItems');
  }
  if (_couponType === 4) {
    discountThresholdText = `${details.discounts_type === 1 ? t('highest') : t('lowest')} ${t('priced')} ${details.number} ${details.number === 1 ? t('item') : t('items')}`;
  }
  return discountThresholdText;
}

function getCouponDescription(coupon: any, t: TFunction, isShowValidDate = true) {
  const type = getCouponType(coupon);

  let shippingMethod = '';

  // 运费券
  if (type === ECouponType.SHIPPING) {
    const couponDetails = getCouponDetails(coupon);
    const { express_method } = couponDetails;

    const expressMethodMap = {
      '1': t('standardShipping'),
      '2': t('advancedShipping'),
      '3': t('businessShipping'),
    };

    shippingMethod = expressMethodMap[express_method];
  }
  let { time_interval = [] } = coupon;

  let validDate = '';

  if (coupon.is_get === 1) {
    time_interval = [coupon.start_time, coupon.end_time];
  }
  if (time_interval.length >= 1 && !!time_interval[1]) {
    validDate = `${time_interval[0]} - ${time_interval[1]}`;
  }
  if (!time_interval[0] && !time_interval[1]) {
    validDate = t('noExpiryDate');
  }
  return shippingMethod
    ? `<div id="shipping-method">${shippingMethod}</div>${isShowValidDate ? `<div class="mt-6 sm:mt-8">${validDate}</div>` : ''}`
    : validDate;
}

export function getCouponType(coupon: any) {
  const type = Number(coupon.model_type);

  // 1 和2 都算作是1
  const typeMap = {
    1: ECouponType.DISCOUNT,
    2: ECouponType.DISCOUNT,
    3: ECouponType.EXCHANGE,
    4: ECouponType.SHIPPING,
  };

  return typeMap[type] || ECouponType.DISCOUNT;
}

function getCouponCopyWriting(coupon: any, t: TFunction) {
  // 处理运费券的信息展示以及其他类型优惠券的copyWriting文案展示

  const type = getCouponType(coupon);
  const details = getCouponDetails(coupon);

  // 运费券
  if (type === ECouponType.SHIPPING && details.objective_country?.length > 0) {
    const country = details.objective_country.map((item: string) => item).join(' , ');
    return t('availableCountry', { country });
  } else if (coupon.copywriting?.length > 0) {
    return coupon.copywriting.map((text: string) => text).join('<br/>');
  }

  return '';
}

// function isShowCountDown(coupon: any) {
//   // 已领取 && 展示倒计时 && 倒计时未结束
//   const condition1 = coupon.status === 2; // 已领取
//   const condition2 = coupon.count_down === 1; // 展示倒计时
//   const condition3 = coupon.twenty_four_count_time > 0; // 倒计时未结束
//   return condition1 && condition2 && condition3;
// }

const getCouponUIType = (coupon: any, disabled: boolean) => {
  if (disabled) return ECouponUIType.DISABLED;
  if (coupon.count_down === 1) return ECouponUIType.COUNT_DOWN;
  if (coupon.model_type === 4) return ECouponUIType.SHIPPING;
  return ECouponUIType.NORMAL;
};

export const couponDateTransform = (
  _coupon: any,
  disabled: boolean,
  t?: TFunction,
  currency?: any,
  isNeedTranslate?: boolean,
  isShowValidDate?: boolean,
): ICouponProps => {
  const coupon = _coupon.discount_code ? _coupon.discount_code : _coupon;
  // 优惠券 UI 类型

  const couponUIType = getCouponUIType(coupon, disabled);

  const type = getCouponType(coupon);
  const disCountText = getDiscountText(coupon, t, currency, isNeedTranslate);
  const title = getCouponTitle(coupon, t, currency);
  const description = getCouponDescription(coupon, t, isShowValidDate);
  const isStairs = type === ECouponType.DISCOUNT && getCouponDetails(coupon).discounts_type === 2;

  const ret: ICouponProps = {
    id: coupon.id,
    couponUIType,
    disCountText,
    title,
    description,
    isStairs,
    isFirstOrder: coupon.is_first_order === 1,
    couponType: type,
    buttonText: coupon.is_get,
    copyWriting: getCouponCopyWriting(coupon, t),
    couponName: coupon.name,
    extension_field: coupon.extension_field,
    paymentParams: {
      id: _coupon.id,
      code: _coupon.code,
      discount_code: _coupon.discount_code,
    },
  };
  return ret;
};

interface DiscountStair {
  discounts_value: number;
  threshold: number;
}

interface DiscountDetails {
  discounts_type: 1 | 2;
  discounts_value?: number;
  discounts_threshold?: number;
  discounts_threshold_type?: 1 | 2;
  discounts_stairs?: DiscountStair[];
  discounts_stairs_type?: 1 | 2;
  number?: number;
}

interface CouponData {
  id: number;
  way: 1 | 2 | 3 | 4 | 5;
  discounts_details: DiscountDetails;
  full_discounts_details: DiscountDetails;
  appoint_discounts_details: DiscountDetails;
  free_discounts_details: DiscountDetails;
}

/**
 * !!使用此方法前 先判断是否有优惠券数据再调用
 * @param obj 优惠券数据
 * @returns string 优惠券展示文案，如果是阶梯优惠券，中间使用','分割
 * 例：'5% OFF; Orders $29+,10% OFF; Orders $59+,15% OFF; Orders $79+'
 */
export function getDiscountCouponText(obj: CouponData, t: TFunction, currency: Currency): string {
  if (obj.way === 1 || obj.way === 5) {
    return handleCouponContent(obj.way, obj.discounts_details, obj, t, currency);
  } else if (obj.way === 2) {
    return handleCouponContent(obj.way, obj.full_discounts_details, obj, t, currency);
  } else if (obj.way === 3) {
    return handleCouponContent(obj.way, obj.appoint_discounts_details, obj, t, currency);
  } else {
    return handleCouponContent(obj.way, obj.free_discounts_details, obj, t, currency);
  }
}

// 在于服务端渲染还是客户端渲染，影响了currency的获取
// TODO: 为了避免改动太大，这里的currency没去动，默认还是服务端组件
export function handleCouponContent(
  way: number,
  details: DiscountDetails,
  obj: CouponData,
  t: TFunction,
  currency: Currency,
): string {
  let textLeft = '';
  let textMiddle = '';
  let textStairTop0 = '';
  let textStairBottom0 = '';
  let textStairTop1 = '';
  let textStairBottom1 = '';
  let textStairTop2 = '';
  let textStairBottom2 = '';
  if (way === 1 || way === 5) {
    if (details.discounts_type === 1) {
      textLeft = `${details.discounts_value}% ${t('off')}; `;

      if (details.discounts_threshold_type === 1) {
        textMiddle =
          Number(details.discounts_threshold) === 0.01
            ? t('sitewideSale')
            : t('ordersOver', {
                price: formatPriceNumberWithSymbol(
                  `${details.discounts_threshold}`,
                  currency,
                  false,
                  2,
                  false,
                ),
              });
      } else if (details.discounts_threshold_type === 2) {
        textMiddle =
          Number(details.discounts_threshold) === 1
            ? t('sitewideSale')
            : `${details.discounts_threshold}+ ${t('items')}`;
      }
      return textLeft + textMiddle;
    } else if (details.discounts_type === 2 && details.discounts_stairs) {
      if (details.discounts_stairs.length === 1) {
        textStairTop0 = `${details.discounts_stairs[0].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom0 = `${details.discounts_stairs[0].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom0 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[0].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        return textStairTop0 + textStairBottom0;
      } else if (details.discounts_stairs.length === 2) {
        textStairTop0 = `${details.discounts_stairs[0].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom0 = `${details.discounts_stairs[0].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom0 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[0].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        textStairTop1 = `${details.discounts_stairs[1].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom1 = `${details.discounts_stairs[1].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom1 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[1].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        return `${textStairTop0 + textStairBottom0},${textStairTop1}${textStairBottom1}`;
      } else if (details.discounts_stairs.length >= 3) {
        textStairTop0 = `${details.discounts_stairs[0].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom0 = `${details.discounts_stairs[0].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom0 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[0].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        textStairTop1 = `${details.discounts_stairs[1].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom1 = `${details.discounts_stairs[1].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom1 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[1].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        textStairTop2 = `${details.discounts_stairs[2].discounts_value}% ${t('off')}; `;
        if (details.discounts_stairs_type === 2) {
          textStairBottom2 = `${details.discounts_stairs[2].threshold}+ ${t('items')}`;
        } else if (details.discounts_stairs_type === 1) {
          textStairBottom2 = t('ordersOver', {
            price: formatPriceNumberWithSymbol(
              `${details.discounts_stairs[2].threshold}`,
              currency,
              false,
              2,
              false,
            ),
          });
        }
        return `${textStairTop0 + textStairBottom0},${textStairTop1}${textStairBottom1},${
          textStairTop2
        }${textStairBottom2}`;
      }
    }
  } else if (way === 2) {
    if (details.discounts_type === 1) {
      textLeft = `${formatPriceNumberWithSymbol(`${details.discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
      // 针对金额满减优惠券，进行处理
      textMiddle = fullCouponText(details, obj.id, t, currency);
      return textLeft + textMiddle;
    } else if (details.discounts_type === 2 && details.discounts_stairs) {
      if (details.discounts_stairs.length === 1) {
        textStairTop0 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[0].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom0 = t('forOrdersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[0].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        return textStairTop0 + textStairBottom0;
      } else if (details.discounts_stairs.length === 2) {
        textStairTop0 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[0].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom0 = t('ordersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[0].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        textStairTop1 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[1].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom1 = t('ordersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[1].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        return `${textStairTop0 + textStairBottom0},${textStairTop1}${textStairBottom1}`;
      } else if (details.discounts_stairs.length >= 3) {
        textStairTop0 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[0].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom0 = t('ordersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[0].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        textStairTop1 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[1].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom1 = t('ordersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[1].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        textStairTop2 = `${formatPriceNumberWithSymbol(`${details.discounts_stairs[2].discounts_value}`, currency, false, 2, false)} ${t('off')}; `;
        textStairBottom2 = t('ordersOver', {
          price: formatPriceNumberWithSymbol(
            `${details.discounts_stairs[2].threshold}`,
            currency,
            false,
            2,
            false,
          ),
        });
        return `${textStairTop0 + textStairBottom0},${textStairTop1}${textStairBottom1},${
          textStairTop2
        }${textStairBottom2}`;
      }
    }
  } else if (way === 3) {
    textLeft = `${t('salePrice', { price: formatPriceNumberWithSymbol(`${details.discounts_value}`, currency, false, 2, false) })}; `;
    textMiddle = t('onSelectedItems');
    return textLeft + textMiddle;
  } else if (way === 4 && details.number !== undefined) {
    textLeft = `${t('free')}; `;
    if (details.discounts_type === 1) {
      textMiddle = `${t('highest')} ${t('pricedItems', {
        type: t('highest'),
        number: details.number,
        item: Number(details.number) === 1 ? t('item') : t('items'),
      })}`;
    } else {
      textMiddle = `${t('lowest')} ${t('pricedItems', {
        type: t('lowest'),
        number: details.number,
        item: Number(details.number) === 1 ? t('item') : t('items'),
      })}`;
    }
    return textLeft + textMiddle;
  }
  return '';
}

function fullCouponText(
  details: DiscountDetails,
  discount_code_id: number,
  t: TFunction,
  currency: Currency,
): string {
  // 问卷提交成功获取的优惠券，文案特殊处理
  const discountCodeId = env.NEXT_PUBLIC_QUEST_COUPON_ID ?? 10177;
  if (discount_code_id === discountCodeId) {
    return t('noThresholdFreeShippingCoupon');
  }
  // 一次性满减使用门槛为0.01元，文案展示sitewide sale
  // 优惠门槛 - 优惠金额=== 0.01元
  if (
    details.discounts_threshold &&
    details.discounts_value &&
    Number(details.discounts_threshold) * 100 - Number(details.discounts_value) * 100 === 1
  ) {
    return t('sitewideSale');
  }
  return t('ordersOver', {
    price: formatPriceNumberWithSymbol(`${details.discounts_threshold}`, currency, false, 2, false),
  });
}
